import React, { useCallback, useEffect, useRef, useState } from "react";
import { API, searchMedicamentReference } from "../../services/api";

function useBdpmInfo() {
    const [info, setInfo] = useState(null);
    useEffect(() => {
        API.get("/medicaments-reference/info")
            .then(r => setInfo(r.data))
            .catch(() => {});
    }, []);
    return info;
}

function formatDate(isoString) {
    if (!isoString) return "inconnue";
    return new Date(isoString).toLocaleDateString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric"
    });
}

function BdpmTooltip({ info }) {
    const [visible, setVisible] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0, width: 320 });
    const hideTimer = useRef(null);
    const ref = useRef(null);
    const buttonRef = useRef(null);

    const updatePosition = useCallback(() => {
        if (!buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        const viewportWidth = globalThis.innerWidth || 1024;
        const tooltipWidth = Math.min(360, Math.max(280, viewportWidth - 16));
        const left = Math.min(
            Math.max(8, rect.right - tooltipWidth),
            viewportWidth - tooltipWidth - 8
        );
        setPosition({
            top: rect.bottom + 6,
            left,
            width: tooltipWidth
        });
    }, []);

    const show = () => {
        clearTimeout(hideTimer.current);
        updatePosition();
        setVisible(true);
    };
    const hide = () => {
        hideTimer.current = setTimeout(() => setVisible(false), 120);
    };

    useEffect(() => {
        const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setVisible(false); };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    useEffect(() => {
        if (!visible) return undefined;
        updatePosition();
        globalThis.addEventListener("resize", updatePosition);
        globalThis.addEventListener("scroll", updatePosition, true);
        return () => {
            globalThis.removeEventListener("resize", updatePosition);
            globalThis.removeEventListener("scroll", updatePosition, true);
        };
    }, [updatePosition, visible]);

    return (
        <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
            <button
                ref={buttonRef}
                type="button"
                tabIndex={-1}
                onMouseEnter={show}
                onMouseLeave={hide}
                onClick={() => {
                    updatePosition();
                    setVisible(v => !v);
                }}
                title="Source des données"
                style={{
                    width: "18px",
                    height: "18px",
                    borderRadius: "50%",
                    border: "1px solid #94a3b8",
                    background: "#f1f5f9",
                    color: "#64748b",
                    fontSize: "11px",
                    fontWeight: 700,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    lineHeight: 1,
                    padding: 0,
                    flexShrink: 0
                }}
            >?</button>

            {visible && (
                <div
                    onMouseEnter={show}
                    onMouseLeave={hide}
                    style={{
                    position: "fixed",
                    top: `${position.top}px`,
                    left: `${position.left}px`,
                    zIndex: 2000,
                    width: `${position.width}px`,
                    maxWidth: "calc(100vw - 16px)",
                    boxSizing: "border-box",
                    background: "white",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                    padding: "12px 14px",
                    fontSize: "11px",
                    color: "#475569",
                    lineHeight: 1.5,
                    whiteSpace: "normal",
                    overflowWrap: "anywhere"
                }}>
                    <div style={{ fontWeight: 600, color: "#1e293b", marginBottom: "6px", fontSize: "12px" }}>
                        Source des données
                    </div>
                    <div style={{ marginBottom: "4px" }}>
                        {info?.source ?? "Base de données publique des médicaments (ANSM)"}
                    </div>
                    <a
                        href={info?.sourceUrl ?? "https://base-donnees-publique.medicaments.gouv.fr"}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: "#2563eb", wordBreak: "break-all" }}
                    >
                        {info?.sourceUrl ?? "base-donnees-publique.medicaments.gouv.fr"}
                    </a>
                    <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #f1f5f9", color: "#64748b" }}>
                        {info?.mention ?? "Loi n°78-753 du 17 juillet 1978 (loi CADA) – Art. 12"}
                    </div>
                    <div style={{ marginTop: "6px" }}>
                        Dernière mise à jour :{" "}
                        <strong>{info ? formatDate(info.lastSyncAt) : "—"}</strong>
                    </div>
                </div>
            )}
        </div>
    );
}

function suggestionCip13(suggestion) {
    const raw = String(
        suggestion?.cip13
        ?? suggestion?.cip13Code
        ?? suggestion?.cip13Codes
        ?? suggestion?.cip13_codes
        ?? ""
    ).trim();
    return raw.split(/[;,]/)[0].trim();
}

export default function MedicamentAutocomplete({ value, onChange, onSelect, inputStyle, autoFocus, onFocused, onEnterConfirm }) {
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [dropdownPos, setDropdownPos] = useState({ top: null, bottom: null, left: 0, width: 0, maxHeight: 360 });
    const debounceRef = useRef(null);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const info = useBdpmInfo();

    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus();
            onFocused?.();
        }
    }, [autoFocus, onFocused]);

    const updateDropdownPos = useCallback(() => {
        if (!inputRef.current) return;
        const rect = inputRef.current.getBoundingClientRect();
        const spaceBelow = globalThis.innerHeight - rect.bottom - 8;
        const spaceAbove = rect.top - 8;
        const minUsable = 150;
        const dropWidth = Math.min(Math.max(rect.width, 480), globalThis.innerWidth - rect.left - 8);
        if (spaceBelow >= minUsable) {
            setDropdownPos({ top: rect.bottom + 2, bottom: null, left: rect.left, width: dropWidth, maxHeight: Math.max(80, spaceBelow) });
        } else {
            setDropdownPos({ top: null, bottom: globalThis.innerHeight - rect.top + 2, left: rect.left, width: dropWidth, maxHeight: Math.max(80, spaceAbove) });
        }
    }, []);

    const fetchSuggestions = useCallback((q) => {
        clearTimeout(debounceRef.current);
        if (q.length < 2) { setSuggestions([]); setOpen(false); return; }
        debounceRef.current = setTimeout(async () => {
            try {
                const { data } = await searchMedicamentReference(q);
                updateDropdownPos();
                setSuggestions(data);
                setOpen(data.length > 0);
                setActiveIndex(-1);
            } catch {
                setSuggestions([]);
                setOpen(false);
            }
        }, 200);
    }, [updateDropdownPos]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleChange = (e) => {
        onChange(e.target.value);
        fetchSuggestions(e.target.value);
    };

    const handleSelect = (suggestion) => {
        if (onSelect) {
            onSelect(suggestion);
        } else {
            onChange(suggestion.denomination);
        }
        setOpen(false);
        setSuggestions([]);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            if (open && activeIndex >= 0) {
                e.preventDefault();
                handleSelect(suggestions[activeIndex]);
            } else if (!open) {
                e.preventDefault();
                onEnterConfirm?.();
            }
            return;
        }
        if (!open) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex(i => Math.min(i + 1, suggestions.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex(i => Math.max(i - 1, 0));
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    return (
        <div style={{ display: "flex", alignItems: "center", gap: "4px", flex: 1, minWidth: 0 }}>
            <div ref={wrapperRef} style={{ flex: 1, minWidth: 0 }}>
                <input
                    ref={inputRef}
                    style={inputStyle}
                    placeholder="Nom du médicament"
                    value={value || ""}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    autoComplete="off"
                />
                {open && (
                    <ul style={{
                        position: "fixed",
                        top: dropdownPos.top ?? undefined,
                        bottom: dropdownPos.bottom ?? undefined,
                        left: dropdownPos.left,
                        width: dropdownPos.width,
                        zIndex: 9999,
                        margin: 0,
                        padding: 0,
                        listStyle: "none",
                        background: "white",
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        maxHeight: `${dropdownPos.maxHeight}px`,
                        overflowY: "auto"
                    }}>
                        {suggestions.map((s, i) => (
                            <li
                                key={`${s.codeCis}-${suggestionCip13(s) || "sans-cip13"}`}
                                onMouseDown={() => handleSelect(s)}
                                style={{
                                    background: i === activeIndex ? "#eff6ff" : "white",
                                    borderBottom: i < suggestions.length - 1 ? "1px solid #f1f5f9" : "none"
                                }}
                            >
                                <button
                                    type="button"
                                    style={{
                                    display: "block",
                                    width: "100%",
                                    border: "none",
                                    textAlign: "left",
                                    padding: "7px 10px",
                                    fontSize: "13px",
                                    cursor: "pointer",
                                    background: "transparent"
                                }}
                                >
                                <div style={{ display: "flex", gap: "8px", alignItems: "baseline", minWidth: 0 }}>
                                    <span style={{ fontWeight: 500 }}>{s.denomination}</span>
                                    {suggestionCip13(s) && (
                                        <span style={{ color: "#0f766e", fontSize: "11px", whiteSpace: "nowrap" }}>
                                            CIP13: {suggestionCip13(s)}
                                        </span>
                                    )}
                                </div>
                                {s.formePharmaceutique && (
                                    <span style={{ color: "#94a3b8", fontSize: "11px" }}>
                                        {s.formePharmaceutique}
                                    </span>
                                )}
                                {s.presentationLabel && (
                                    <span style={{ display: "block", color: "#64748b", fontSize: "11px" }}>
                                        {s.presentationLabel}
                                    </span>
                                )}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <BdpmTooltip info={info} />
        </div>
    );
}
